/**
 * Free-text input with typeahead suggestions from the product catalog.
 *
 * Unlike `CatalogProductSearch` (a combobox that requires a selection),
 * this is a plain text field that *suggests* catalog products as you type
 * but happily accepts arbitrary text. Useful for forms where the user might
 * reference medications we don't stock (e.g. consent form pre-op questions).
 */
import type { CatalogProduct } from '@snootsvet/shared'

import {
  Autocomplete,
  AutocompleteCollection,
  AutocompleteContent,
  AutocompleteEmpty,
  AutocompleteInput,
  AutocompleteItem,
  AutocompleteList,
} from '@/frontend/components/autocomplete'
import { useListCatalogProducts } from '@/frontend/features/inventory/use-list-catalog-products'
import { useDebounce } from '@/frontend/hooks/use-debounce'
import { cn } from '@/frontend/lib/utils'

interface CatalogProductAutocompleteProps {
  value: string
  onChange: (value: string) => void
  placeholder?: string
  disabled?: boolean
  className?: string
}

/**
 * Text input with catalog product suggestions. The value is always a plain
 * string — selecting a suggestion fills in the product name, but free-text
 * is equally valid.
 */
export function CatalogProductAutocomplete({
  value,
  onChange,
  placeholder = 'Medication name...',
  disabled,
  className,
}: CatalogProductAutocompleteProps) {
  const debouncedQuery = useDebounce(value, 200)
  const { data: products, isLoading } = useListCatalogProducts({
    search: debouncedQuery || undefined,
  })

  const items = products ?? []

  return (
    <Autocomplete<CatalogProduct>
      value={value}
      onValueChange={(v) => onChange(v)}
      items={items}
      itemToStringValue={(p) => p.name}
      mode="list"
      disabled={disabled}
      openOnInputClick={false}
    >
      <AutocompleteInput placeholder={placeholder} className={cn('w-full', className)} />
      <AutocompleteContent>
        <AutocompleteList>
          {isLoading ? (
            <div className="py-2 text-center text-muted-foreground">Loading...</div>
          ) : (
            <>
              <AutocompleteEmpty>No suggestions found.</AutocompleteEmpty>
              <AutocompleteCollection>
                {(product) => (
                  <AutocompleteItem key={product.id} value={product}>
                    <div className="flex flex-col">
                      <span>{product.name}</span>
                      {product.formulation && (
                        <span className="text-sm text-muted-foreground">
                          {product.formulation}
                          {product.unit ? ` · ${product.unit}` : ''}
                        </span>
                      )}
                    </div>
                  </AutocompleteItem>
                )}
              </AutocompleteCollection>
            </>
          )}
        </AutocompleteList>
      </AutocompleteContent>
    </Autocomplete>
  )
}
